<template>
  <div>
    <div class="video-player" v-on:wheel="wheelZoom">
      <slot name="over-player-content"></slot>
      <div v-if="hasPlayed && canZoom" class="zoom-ui">
        <div v-on:click="zoomOut" class="zoom-out"></div>
        <input
          type="range"
          :min="minZoom"
          :max="maxZoom"
          v-model.number="zoom"
          class="slider"
          id="zoom"
        />
        <div v-on:click="zoomIn" class="zoom-in"></div>
      </div>
      <VideoJSComponent
        ref="videoJSComponent"
        :options="videoOptions"
        :autoplay="autoplay"
        v-on:playing="onPlaying"
        v-on:paused="onPaused"
        v-on:ready="onReady"
        v-on:seeked="onSeeked"
        v-on:seeking="onSeeking"
      />
    </div>
    <slot name="below-player-content"></slot>
  </div>
</template>

<script>
import VideoJSComponent from "@/components/VideoJS";
import videojs from "@/vendor/videojs/video.es.js";

export default {
  name: "Player",
  props: {
    video: Object,
    player: Object,
    type: String,
    autoplay: {
      type: Boolean,
      default: false
    },
    pauseVdOnUpdate: {
      type: Boolean,
      default: false
    }
  },
  data: function()  {
    return {
      zoom: 150,
      minZoom: 100,
      maxZoom: 250,
    }
  },
  computed: {
    virtualDirector: {
      get() {
        if (this.video.customVirtualDirector && this.video.customVirtualDirector.length > 0) {
          this.useOffset = false;
          return this.video.customVirtualDirector;
        } else {
          this.useOffset = true;
          return this.video.virtualDirector;
        }
      }
    },
    viewMode: {
      get() {
        return this.player.viewMode;
      },
      set(value) {
        this.player.viewMode = value;
      }
    },
    jumpPosition: {
      get() {
        return this.player.jumpPosition;
      }
    },
    playing: {
      get() {
        return this.player.playing;
      },
      set(value) {
        this.player.playing = value;
        if (value == true) {
          this.hasPlayed = true;
        }
      }
    },
    hasPlayed: {
      get() {
        return this.player.hasPlayed;
      },
      set(value) {
        this.player.hasPlayed = value;
      }
    },
    canZoom: {
      get() {
        return this.player.canZoom;
      },
      set(value) {
        this.player.canZoom = value;
      }
    },
    seeking: {
      get(value) {
        this.player.seeking = value;
      },
      set(value) {
        this.player.seeking = value;
      }
    },
    musicId: {
      get() {
        return this.video.musicId;
      }
    },
    videoOptions() {
      if (this.type == "url") {
        return {
          autoplay: false,
          controls: true,
          sources: [
            {
              src: this.video.stream,
              type: "application/x-mpegURL"
            }
          ]
        };
      } else if (this.type == "json") {
        return {
          autoplay: false,
          controls: true,
          sources: [
            {
              src: `data:application/vnd.videojs.vhs+json,${JSON.stringify(
                this.video.clipManifest
              )}`,
              type: "application/vnd.videojs.vhs+json"
            }
          ]
        };
      }
    }
  },
  beforeDestroy() {
    this.onDestroyOrUpdate();
  },
  updated() {
    if (this.pauseVdOnUpdate == true) {
      this.onDestroyOrUpdate();
    }
  },
  methods: {
    onDestroyOrUpdate() {
      this.$refs.videoJSComponent.videoJSPlayer.hiliteSports.vd.pause();
    },
    zoomIn() {
      if (this.zoom < this.maxZoom) {
        this.zoom += 10;
      }
    },
    zoomOut() {
      if (this.zoom > this.minZoom) {
        this.zoom -= 10;
      }
    },
    onPlaying() {
      this.playing = true;
      if (this.player.swapVMOnPlay) {
        this.viewMode = "VirtualDirector";
      }
    },
    onPaused() {
      this.playing = false;
      if (this.player.swapVMOnPlay) {
        this.viewMode = "Free View";
      }
    },
    onReady() {
      const videoJSPlayer = this.$refs.videoJSComponent.videoJSPlayer;
      let that = this;

      videoJSPlayer.HiShared({
        vd: this.virtualDirector,
        minZoom: this.minZoom,
        maxZoom: this.maxZoom
      });

      videoJSPlayer.HiZoom();
      videoJSPlayer.HiRecorder();
      videoJSPlayer.HiMarkers();
      videoJSPlayer.HiAutozoom();
      videoJSPlayer.HiMusic();
      videoJSPlayer.HiThumbnailGrabber();

      videoJSPlayer.on("loadedmetadata", () => {
        videoJSPlayer.trigger("clearMarkers");

        if (this.video.gameEvents) {
          videoJSPlayer.trigger(
            "addMarkers",
            this.video.gameEvents
          );
        }

        if (this.musicId) {
          console.log("found some music!");
          this.attachMusic(this.musicId);
        }
      });

      this.checkViewMode(this.player.viewMode, true);
    },
    onSeeked() {
      this.seeking = false;
    },
    onSeeking() {
      this.seeking = true;
    },
    wheelZoom(e) {
      e.preventDefault();
      if (this.canZoom) {
        this.zoomEvent = e;
        if (e.deltaY > 0 && this.zoom < this.maxZoom) {
          this.zoom = this.zoom + 3;
        } else if (this.zoom > this.minZoom) {
          this.zoom = this.zoom - 3;
        }
      }
    },
    checkViewMode(viewMode, onLoad = false) {
      const videoJSPlayer = this.$refs.videoJSComponent.videoJSPlayer;

      let checkIt = function() {
        if (viewMode == "VirtualDirector") {
          this.canZoom = false;
          this.$refs.videoJSComponent.videoJSPlayer.trigger("disableZoom");
          this.$refs.videoJSComponent.videoJSPlayer.trigger("setupAutozoom", {timelineData: this.video.virtualDirector, useOffset: this.useOffset});
        } else if (viewMode == "Free View") {
          this.canZoom = true;
          this.$refs.videoJSComponent.videoJSPlayer.trigger("destroyAutozoom");
          this.$refs.videoJSComponent.videoJSPlayer.trigger("enableZoom");
        }
      }

      checkIt = checkIt.bind(this);

      if (onLoad) {
        videoJSPlayer.on("loadedmetadata", checkIt);
      } else {
        checkIt();
      }

    },
    attachMusic(musicId) {
      let clip = _.find(this.$store.state.editor.music, ['id', musicId]);
      if (clip) {
        this.$refs.videoJSComponent.videoJSPlayer.trigger("attachClip", clip);
      } else {
        this.$refs.videoJSComponent.videoJSPlayer.trigger("stopClip", clip);
      }
    }
  },
  watch: {
    musicId: {
      handler(musicId) {
        this.attachMusic(musicId);
      }
    },
    viewMode: {
      handler(viewMode) {
        this.checkViewMode(viewMode);
      }
    },
    canZoom: {
      handler(value) {
        if (value == true) {
          this.$refs.videoJSComponent.videoJSPlayer.trigger("zoom", {
            value: this.zoom,
            animate: false
          });
        }
      }
    },
    zoom: {
      handler(value) {
        this.$refs.videoJSComponent.videoJSPlayer.trigger("zoom", {
          value: value,
          event: this.zoomEvent
        });
      }
    },
    jumpPosition: {
      handler(value) {
        if (value !== false) {
          this.$refs.videoJSComponent.videoJSPlayer.currentTime(value);
          this.player.jumpPosition = false;
        } else {
          return false;
        }
      }
    },
    virtualDirector: {
      handler(value) {
        const videoJSPlayer = this.$refs.videoJSComponent.videoJSPlayer;
        videoJSPlayer.trigger(
          "updateVd",
          {
            vd: value,
            minZoom: this.minZoom,
            maxZoom: this.maxZoom
          }
        );
      }
    }
  },
  components: {
    VideoJSComponent
  }
};
</script>

<style lang="stylus" scoped>
.video-player
  width: 100%
  background: #e5e5e5
  position: relative
  overflow: hidden
  @media screen and (max-width: mobile-landscape)
    width: calc(100% + 40px)
    margin-left: -20px
  &:hover
    .zoom-ui
      right: -40px
  .zoom-ui
    width: 165px
    height: 40px
    background: #222849
    transform: rotate(-90deg)
    right: -120px
    bottom: 0px
    top: 0px
    margin: auto
    border-radius: 5px
    position: absolute
    z-index: 10
    display: flex
    justify-content: center
    align-items: center
    transition: right 250ms
    @media screen and (max-width: mobile-landscape)
      display: none
    input[type="range"]
      width: 80px
      margin: 0px 10px
      background: none
      -webkit-appearance: none
      background: transparent
      &::-moz-range-thumb
        border: none
        height: 12px
        width: 2px
        background: #ffffff
        cursor: pointer
        margin-top: -14px
      &::-moz-range-track
        width: 100%
        height: 2px
        cursor: pointer
        background: #FFF
      &::-webkit-slider-thumb
        -webkit-appearance: none
        border: none
        width: 2px
        height: 12px
        background: #ffffff
        cursor: pointer
        margin-top: -5px
      &::-webkit-slider-runnable-track
        width: 100%
        height: 2px
        cursor: pointer
        background: #FFF
        border: none
      &::-ms-thumb
        box-shadow: none
        border: none
        height: 20px
        width: 2px
        background: #ffffff
        cursor: pointer
      &::-ms-track
        width: 100%
        height: 2px
        cursor: pointer
        background: transparent
        border-color: transparent
        color: transparent
      &::-ms-fill-lower {
        background: #FFFFFF
        border: none
      }
      &:focus::-ms-fill-lower {
        background: #FFFFFF
      }
      &::-ms-fill-upper {
        background: #FFFFFF
      }
      &:focus::-ms-fill-upper {
        background: #FFFFFF
      }
    .zoom-in, .zoom-out
      width: 20px
      height: 20px
      cursor: pointer
      background-size: 8px auto
      background-repeat: no-repeat
      background-position: center center
      border-radius: 20px
      border: 1px solid rgba(255,255,255,.5)
      transition: background 250ms
      &:hover
        background-color: rgba(0,0,0,0.85)
    .zoom-in
      background-image:url('../assets/images/plus.svg')
    .zoom-out
      background-image: url('../assets/images/minus.svg')
      transform: rotate(90deg)
</style>

<style lang="stylus">
.vjs-control-bar
  display: flex!important
  visibility: visible!important
  opacity: 1!important
  transition: visibility 0.1s, opacity 0.1s!important
.vjs-big-play-button
  width: 100%!important
  height: 100%!important
  border-radius: 0px!important
  left: 0px!important
  top: 0px!important
  margin: 0px!important
  .vjs-icon-placeholder
    position: absolute
    left: 0px
    right: 0px
    top: 0px
    bottom: 0px
    margin: auto
    width: 42px
    height: 42px
    line-height: 100%
    font-size: 42px
</style>
